import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RichMediaState } from '@bonusly/common-action-input';
import { useHttpClient } from '@bonusly/http-client';
import { Recipient, RecipientAPIData } from '@bonusly/recipient-entities';
import { useCurrentUser, useFeatureFlag } from '@bonusly/who-am-i';

import { isEmailIdentifier } from '../../../../utils/email';

type FetchRecipientsProps = {
	include_self?: boolean;
	include_non_receivable?: boolean;
	search_usergroups?: boolean;
	search_users?: boolean;
};

type FetchUseRecipientsProps = {
	query: string;
	state: RichMediaState;
};

type RecipientAPIResponse = {
	data?: {
		result?: RecipientAPIData[] | null;
	};
};

const normalizeInviteEmail = (value: string) => value.trim().replace(/^@+/, '').toLowerCase();

const isInviteEmailQuery = (value: string) => isEmailIdentifier(normalizeInviteEmail(value));

const getRecipientResult = (response: RecipientAPIResponse | null | undefined): RecipientAPIData[] => {
	const result = response?.data?.result;

	return Array.isArray(result) ? result : [];
};

// @TODO reuse this logic for other common (feedback be sure not include groups)
export const useFetchRecipients = (params: FetchRecipientsProps = {}) => {
	const currentUser = useCurrentUser();
	const inviteByEmailEnabled = useFeatureFlag('plg-invite-by-email');
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');

	return useCallback(
		async ({ query, state }: FetchUseRecipientsProps): Promise<Recipient[]> => {
			const search = query.trim();
			const taggedRecipients = [...state.mentions, ...state.recipients];
			const { include_self, ...rest } = params;
			const inviteEmail = normalizeInviteEmail(search);
			const shouldSuggestEmailInvite = inviteByEmailEnabled && isInviteEmailQuery(search);

			const buildRecipientsList = (data: RecipientAPIData[]) => {
				const recipients: Recipient[] = [];
				const seen = new Set<string>();
				data.forEach((r) => {
					const recipient = new Recipient(r);
					const dedupeKey = `${recipient.type}:${recipient.id}`;
					if (seen.has(dedupeKey)) return;
					if (!include_self && recipient.type === 'username' && recipient.id === currentUser.id) {
						return;
					}
					if (!include_self && recipient.type === 'group') {
						// exclude self user from groups
						const recipients = recipient.userIds.filter((id) => id !== currentUser.id);
						recipient.userIds = recipients;
						// exclude group if empty
						if (recipients.length === 0) return;
					}
					if (taggedRecipients.some((tagged) => tagged.id === recipient.id)) {
						return;
					}
					seen.add(dedupeKey);
					recipients.push(recipient);
				});
				return recipients;
			};

			if (!search) {
				const data = await queryClient.fetchQuery(['recipients'], () => http.get('/recipients'), { staleTime: Infinity });
				return buildRecipientsList(getRecipientResult(data));
			}

			const all = { search, ...rest };
			const data = await queryClient.fetchQuery(['rich-users', all], () => {
				return http.get('/users/autocomplete', { params: all });
			});

			const recipients = buildRecipientsList(getRecipientResult(data));
			const hasExactEmailMatch = getRecipientResult(data).some((recipient: any) => {
				const candidate = (recipient.email || recipient.username || '').toString().toLowerCase();
				return candidate === inviteEmail;
			});
			const isTaggedAlready = taggedRecipients.some((tagged) => {
				return (tagged.id || tagged.username || '').toString().toLowerCase() === inviteEmail;
			});
			const currentUserEmail = ((currentUser as any).email || '').toString().toLowerCase();
			const isCurrentUserEmail = currentUserEmail === inviteEmail;

			if (shouldSuggestEmailInvite && !hasExactEmailMatch && !isTaggedAlready && (include_self || !isCurrentUserEmail)) {
				recipients.unshift(
					new Recipient({
						id: inviteEmail,
						display_name: inviteEmail,
						profile_pic_url: '',
						username: inviteEmail,
						user_mode: 'email',
					})
				);
			}

			return recipients;
		},
		[currentUser, http, inviteByEmailEnabled, params, queryClient]
	);
};
